<template>
  <v-dialog v-model="dialog" persistent max-width="720px">
    <v-card class="vendor-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">New Vendor</div>
          <div class="dialog-subtitle">Add vendor contact and address details.</div>
        </div>

        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12" md="6">
            <div class="section-title">Contact Information</div>

            <div class="field-label mt-3">Name</div>
            <v-text-field
              ref="vendorName"
              v-model="vendor.name"
              outlined
              dense
              label="Name"
              :hide-details="nameErrors.length === 0"
              :error-messages="nameErrors"
            />

            <div class="field-label mt-3">Contact Name</div>
            <v-text-field
              v-model="vendor.contactName"
              outlined
              dense
              hide-details
              label="Contact Name"
              prepend-inner-icon="mdi-account-outline"
            />

            <div class="field-label mt-3">Phone Number</div>
            <v-text-field
              v-model="vendor.phoneNumber"
              outlined
              dense
              hide-details
              label="Phone Number"
              prepend-inner-icon="mdi-phone-outline"
            />

            <div class="field-label mt-3">Email Address</div>
            <v-text-field
              v-model="vendor.email"
              outlined
              dense
              hide-details
              label="Email Address"
              prepend-inner-icon="mdi-email-outline"
            />
          </v-col>

          <v-col cols="12" md="6">
            <div class="section-title">Address & Notes</div>

            <div class="field-label mt-3">Address</div>
            <v-text-field
              v-model="vendor.address"
              outlined
              dense
              hide-details
              label="Address"
              prepend-inner-icon="mdi-map-marker-outline"
            />

            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="field-label mt-3">City</div>
                <v-text-field
                  v-model="vendor.city"
                  outlined
                  dense
                  hide-details
                  label="City"
                />
              </v-col>

              <v-col cols="12" sm="3">
                <div class="field-label mt-3">State</div>
                <v-text-field
                  v-model="vendor.state"
                  outlined
                  dense
                  hide-details
                  label="State"
                />
              </v-col>

              <v-col cols="12" sm="3">
                <div class="field-label mt-3">Zip</div>
                <v-text-field
                  v-model="vendor.zip"
                  outlined
                  dense
                  hide-details
                  label="Zip"
                />
              </v-col>
            </v-row>

            <div class="field-label mt-3">Note</div>
            <v-textarea
              v-model="vendor.note"
              outlined
              dense
              hide-details
              auto-grow
              rows="3"
              label="Note"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn color="primary" text @click="close">Close</v-btn>
        <v-btn color="primary" depressed rounded :loading="loading" dark @click="submit">
          Save Vendor
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import vendorService from "../service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "create-vendor",

  props: {
    createDialog: {
      default: false,
    },
    createFromStore: {
      type: Boolean,
    },
  },

  mixins: [validationMixin],

  data() {
    return {
      loading: false,
      dialog: false,
      vendor: this.getEmptyVendor(),
    };
  },

  validations: {
    vendor: {
      name: {
        required,
      },
    },
  },

  watch: {
    createDialog(val) {
      this.dialog = val;

      if (val) {
        this.$nextTick(() => {
          if (this.$refs.vendorName) {
            this.$refs.vendorName.focus();
          }
        });
      }
    },
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),

    nameErrors() {
      const errors = [];

      if (!this.$v.vendor.name.$dirty) {
        return errors;
      }

      if (!this.$v.vendor.name.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      return errors;
    },
  },

  methods: {
    getEmptyVendor() {
      return {
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      };
    },

    close() {
      this.empty();
      this.dialog = false;
      this.$emit("close");
    },

    empty() {
      this.vendor = this.getEmptyVendor();
      this.$v.$reset();
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      this.loading = true;

      return vendorService
        .create(this.vendor)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Vendor is created",
              });

            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          const message =
            err && err.data && err.data.message
              ? err.data.message
              : "Unable to create vendor";

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: message,
            });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.vendor-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.section-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.dialog-actions {
  padding: 12px 16px;
}
</style>
